import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { facilityAPI } from '../services/api';
import { Users, Plus, Edit2, Trash2, Phone, Mail, Building2, X } from 'lucide-react';
import { toast } from 'react-toastify';

const CONTACT_TYPES = [
  { value: 'agency', label: 'Regulatory Agency', color: '#0066FF', bg: '#EFF6FF' },
  { value: 'inspector', label: 'Inspector', color: '#7C3AED', bg: '#F5F3FF' },
  { value: 'consultant', label: 'Consultant', color: '#16A34A', bg: '#F0FDF4' },
  { value: 'internal', label: 'Internal', color: '#D97706', bg: '#FFFBEB' },
  { value: 'other', label: 'Other', color: '#6B7280', bg: '#F8FAFC' },
];

function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [filterFacility, setFilterFacility] = useState('');
  const [filterType, setFilterType] = useState('');
  const [formData, setFormData] = useState({
    facilityId: '', contactType: 'agency', name: '', title: '',
    organization: '', email: '', phone: '', address: '', notes: ''
  });

  useEffect(() => { loadData(); }, [filterFacility, filterType]);

  const loadData = async () => {
    try {
      const [contactsRes, facilitiesRes] = await Promise.all([
        api.get(`/contacts${filterFacility ? `?facilityId=${filterFacility}` : ''}`),
        facilityAPI.getAll()
      ]);
      let contacts = contactsRes.data.contacts || [];
      if (filterType) contacts = contacts.filter(c => c.contact_type === filterType);
      setContacts(contacts);
      setFacilities(facilitiesRes.data.facilities || []);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await api.put(`/contacts/${editingContact.id}`, formData);
        toast.success('Contact updated!');
      } else {
        await api.post('/contacts', formData);
        toast.success('Contact added!');
      }
      setShowModal(false);
      setEditingContact(null);
      resetForm();
      loadData();
    } catch (err) {
      toast.error('Failed to save contact');
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      facilityId: contact.facility_id || '',
      contactType: contact.contact_type || 'agency',
      name: contact.name || '',
      title: contact.title || '',
      organization: contact.organization || '',
      email: contact.email || '',
      phone: contact.phone || '',
      address: contact.address || '',
      notes: contact.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success('Contact deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => setFormData({
    facilityId: '', contactType: 'agency', name: '', title: '',
    organization: '', email: '', phone: '', address: '', notes: ''
  });

  const getTypeStyle = (type) => CONTACT_TYPES.find(t => t.value === type) || CONTACT_TYPES[4];

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  // Group by type
  const grouped = {};
  contacts.forEach(c => {
    const type = c.contact_type || 'other';
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(c);
  });

  const inputStyle = {
    width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0',
    borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-body)', boxSizing: 'border-box'
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div className="page-title">Contacts</div>
          <div className="page-subtitle">Regulatory agencies, inspectors, consultants, and internal contacts</div>
        </div>
        <button onClick={() => { setEditingContact(null); resetForm(); setShowModal(true); }} className="btn btn-primary">
          <Plus size={15} /> Add Contact
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        <select value={filterFacility} onChange={e => setFilterFacility(e.target.value)}
          style={{ ...inputStyle, width: 'auto', minWidth: 200 }}>
          <option value="">All facilities</option>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ ...inputStyle, width: 'auto', minWidth: 160 }}>
          <option value="">All types</option>
          {CONTACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* Empty state */}
      {contacts.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}>
          <Users size={48} color="#CBD5E1" style={{ margin: '0 auto 16px', display: 'block' }} />
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: '#0A1628', marginBottom: 8 }}>No contacts yet</div>
          <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 24 }}>Add your state agency inspector, consultant, and internal EHS contacts</div>
          <button onClick={() => setShowModal(true)} className="btn btn-primary"><Plus size={15} /> Add First Contact</button>
        </div>
      )}

      {/* Grouped contacts */}
      {Object.keys(grouped).map(type => {
        const typeStyle = getTypeStyle(type);
        return (
          <div key={type} style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color: typeStyle.color, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{typeStyle.label}</span>
              <span style={{ background: '#E2E8F0', borderRadius: 20, padding: '1px 8px', fontSize: 12, color: '#6B7280', fontFamily: 'var(--font-body)' }}>{grouped[type].length}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
              {grouped[type].map(contact => (
                <div key={contact.id} className="card" style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: typeStyle.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: typeStyle.color }}>
                      {getInitials(contact.name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: '#0A1628', marginBottom: 2 }}>{contact.name}</div>
                      {contact.title && <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)' }}>{contact.title}</div>}
                      {contact.organization && <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)' }}>{contact.organization}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button onClick={() => handleEdit(contact)} className="icon-btn"><Edit2 size={13} /></button>
                      <button onClick={() => handleDelete(contact.id)} className="icon-btn" style={{ color: '#DC2626', borderColor: '#FECACA' }}><Trash2 size={13} /></button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {contact.email && (
                      <a href={`mailto:${contact.email}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#0066FF', textDecoration: 'none', fontFamily: 'var(--font-body)' }}>
                        <Mail size={12} /> {contact.email}
                      </a>
                    )}
                    {contact.phone && (
                      <a href={`tel:${contact.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151', textDecoration: 'none', fontFamily: 'var(--font-body)' }}>
                        <Phone size={12} /> {contact.phone}
                      </a>
                    )}
                    {contact.facility_name && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#94a3b8', fontFamily: 'var(--font-body)' }}>
                        <Building2 size={12} /> {contact.facility_name}
                      </div>
                    )}
                  </div>

                  {contact.notes && (
                    <div style={{ marginTop: 10, padding: '8px 10px', background: '#F8FAFC', borderRadius: 6, fontSize: 12, color: '#6B7280', fontFamily: 'var(--font-body)', fontStyle: 'italic' }}>
                      {contact.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: '#0A1628' }}>{editingContact ? 'Edit Contact' : 'Add Contact'}</div>
              <button onClick={() => { setShowModal(false); setEditingContact(null); }} className="icon-btn"><X size={16} /></button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '14px 18px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className="form-label">Contact Type</label>
                  <select value={formData.contactType} onChange={e => setFormData({...formData, contactType: e.target.value})} style={inputStyle}>
                    {CONTACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Facility (optional)</label>
                  <select value={formData.facilityId} onChange={e => setFormData({...formData, facilityId: e.target.value})} style={inputStyle}>
                    <option value="">All facilities / General</option>
                    {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Full Name *</label>
                <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Jane Smith" style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className="form-label">Title / Role</label>
                  <input type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="Air Quality Inspector" style={inputStyle} />
                </div>
                <div>
                  <label className="form-label">Organization</label>
                  <input type="text" value={formData.organization} onChange={e => setFormData({...formData, organization: e.target.value})} placeholder="Ohio EPA" style={inputStyle} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className="form-label">Email</label>
                  <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="jane@ohio.gov" style={inputStyle} />
                </div>
                <div>
                  <label className="form-label">Phone</label>
                  <input type="text" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="(614) 555-0100" style={inputStyle} />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Address</label>
                <input type="text" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="50 W. Town Street, Columbus, OH 43215" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label className="form-label">Notes</label>
                <textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} placeholder="Any additional notes..." style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" onClick={() => { setShowModal(false); setEditingContact(null); }} className="btn btn-ghost">Cancel</button>
                <button type="submit" className="btn btn-primary">{editingContact ? 'Update' : 'Add'} Contact</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`
        @media (max-width: 768px) {
          .mobile-stack { flex-direction: column !important; }
          .mobile-full { width: 100% !important; min-width: 0 !important; }
          .mobile-hide { display: none !important; }
          .mobile-grid-1 { grid-template-columns: 1fr !important; }
          .mobile-grid-2 { grid-template-columns: repeat(2, 1fr) !important; }
          .mobile-p { padding: 12px 14px !important; }
          .mobile-font { font-size: 13px !important; }
        }
      `}</style>
    </div>
  );
}

export default Contacts;
